import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { useFocusMode } from '@/hooks/useFocusMode';
import { NotificationCard } from './NotificationCard';
import { NotificationGroup } from './NotificationGroup';
import { FocusModeSelector } from './FocusModeSelector';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Notification } from '@/types/notifications';
import { formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { useWindowManager } from '@/contexts/WindowManager';

interface NotificationCenterPanelProps {
  onClose: () => void;
}

type TabType = 'all' | 'unread' | 'scheduled';

/**
 * Enhanced Notification Center Panel
 * Alexander McQueen-inspired luxury design with glassmorphism
 */
export const NotificationCenterPanel: React.FC<NotificationCenterPanelProps> = ({
  onClose,
}) => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    archiveNotification: dismissNotification,
  } = useNotifications();

  const { openWindow } = useWindowManager();

  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [isFocusModeOpen, setIsFocusModeOpen] = useState(false);
  const [groupByApp, setGroupByApp] = useState(true);

  // Close panel on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    switch (activeTab) {
      case 'unread':
        return notifications.filter(n => !n.is_read);
      case 'scheduled':
        return notifications.filter(n => n.scheduled_for && !n.delivered_at);
      default:
        return notifications;
    }
  }, [notifications, activeTab]);

  // Group notifications by date
  const groupedByDate = useMemo(() => {
    const groups: {
      today: Notification[];
      yesterday: Notification[];
      earlier: Notification[];
    } = {
      today: [],
      yesterday: [],
      earlier: [],
    };

    filteredNotifications.forEach(notification => {
      const date = parseISO(notification.created_at);
      if (isToday(date)) {
        groups.today.push(notification);
      } else if (isYesterday(date)) {
        groups.yesterday.push(notification);
      } else {
        groups.earlier.push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  // Group notifications by app (within each date group)
  // Memoized to prevent unnecessary recalculations
  const groupNotificationsByApp = useCallback((notificationList: Notification[]) => {
    if (!groupByApp) return [];

    const grouped = notificationList.reduce((acc, notification) => {
      const key = notification.group_key || notification.app_name || 'general';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(notification);
      return acc;
    }, {} as Record<string, Notification[]>);

    return Object.entries(grouped).map(([groupKey, notifs]) => ({
      groupKey,
      notifications: notifs,
      appName: notifs[0].app_name || 'Notifications',
      appIcon: notifs[0].app_icon,
    }));
  }, [groupByApp]);

  // Handle notification actions
  const handleActionClick = (notificationId: string, actionId: string) => {
    console.log('Action clicked:', notificationId, actionId);
    // TODO: Implement action handlers
  };

  const handleSnooze = (notificationId: string, minutes: number) => {
    console.log('Snooze notification:', notificationId, minutes);
    // TODO: Implement snooze logic
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleClearAll = () => {
    filteredNotifications.forEach(n => dismissNotification(n.id));
  };

  // Render date section
  const renderDateSection = (title: string, notificationList: Notification[]) => {
    if (notificationList.length === 0) return null;

    const groups = groupNotificationsByApp(notificationList);
    const hasMultipleGroups = groups.length > 1;

    return (
      <div className="mb-4">
        {/* Date Header */}
        <div className="px-4 py-2 mb-2">
          <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">
            {title}
          </h3>
        </div>

        {/* Notifications */}
        <div className="px-3 space-y-2">
          {hasMultipleGroups && groupByApp ? (
            // Grouped by app
            groups.map(group => (
              <NotificationGroup
                key={group.groupKey}
                groupKey={group.groupKey}
                notifications={group.notifications}
                appName={group.appName}
                appIcon={group.appIcon}
                onMarkAsRead={markAsRead}
                onDismiss={dismissNotification}
                onSnooze={handleSnooze}
                onActionClick={handleActionClick}
                onMarkAllAsRead={() => {
                  group.notifications.forEach(n => markAsRead(n.id));
                }}
              />
            ))
          ) : (
            // Individual notifications
            notificationList.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <NotificationCard
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismissNotification}
                  onSnooze={handleSnooze}
                  onActionClick={handleActionClick}
                />
              </motion.div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Empty state
  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-16 px-6"
    >
      <div className="relative mb-6">
        {/* Decorative background circles */}
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute inset-0 bg-blue-500/20 rounded-full blur-2xl"
        />
        
        {/* Icon container */}
        <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center backdrop-blur-xl">
          <Icons.Bell className="w-10 h-10 text-white/40" />
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-900"
          />
        </div>
      </div>

      <h3 className="text-lg font-semibold text-white mb-2">
        All caught up!
      </h3>
      <p className="text-sm text-white/60 text-center max-w-xs">
        {activeTab === 'unread' && 'No unread notifications'}
        {activeTab === 'scheduled' && 'No scheduled notifications'}
        {activeTab === 'all' && 'You have no notifications'}
      </p>

      {/* Decorative elements */}
      <div className="mt-8 flex gap-2">
        {[1, 2, 3].map((i) => (
          <motion.div
            key={i}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-blue-400/30"
          />
        ))}
      </div>
    </motion.div>
  );

  const totalNotifications = filteredNotifications.length;

  return (
    <div className="w-full">
      {/* Panel */}
      <div className="w-[420px]">
          <div className={cn(
            'rounded-2xl overflow-hidden',
            'backdrop-blur-xl bg-gradient-to-br from-gray-900/95 to-gray-800/95',
            'border border-white/10',
            'shadow-[0_20px_60px_rgba(0,0,0,0.5)]'
          )}>
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-white">Notifications</h2>
                  {unreadCount > 0 && (
                    <p className="text-xs text-white/60">
                      {unreadCount} unread
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Focus Mode Selector */}
                  <FocusModeSelector
                    isOpen={isFocusModeOpen}
                    onToggle={() => setIsFocusModeOpen(!isFocusModeOpen)}
                  />

                  {/* Settings */}
                  <button
                    onClick={() => {
                      // Import NotificationSettingsWindow dynamically
                      import('@/components/windows/NotificationSettingsWindow').then(mod => {
                        openWindow(
                          'notification-settings',
                          'Notification Settings',
                          <mod.default />,
                          {
                            position: { x: 200, y: 100 },
                            size: { width: 900, height: 700 },
                            appType: 'system'
                          }
                        );
                      });
                      onClose(); // Close the panel when opening settings
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/70 hover:text-white"
                    title="Notification Settings"
                  >
                    <Icons.Settings className="w-4 h-4" />
                  </button>

                  {/* Close */}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/70 hover:text-white"
                  >
                    <Icons.X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
                {[
                  { id: 'all' as TabType, label: 'All', count: notifications.length },
                  { id: 'unread' as TabType, label: 'Unread', count: unreadCount },
                  { id: 'scheduled' as TabType, label: 'Scheduled', count: notifications.filter(n => n.scheduled_for).length },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
                      activeTab === tab.id
                        ? 'bg-white/10 text-white shadow-sm'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    )}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={cn(
                        'ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold',
                        activeTab === tab.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/10 text-white/60'
                      )}>
                        {tab.count > 99 ? '99+' : tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[calc(100vh-16rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Icons.Loader2 className="w-8 h-8 text-blue-400" />
                  </motion.div>
                </div>
              ) : totalNotifications === 0 ? (
                renderEmptyState()
              ) : (
                <div className="py-2">
                  {renderDateSection('Today', groupedByDate.today)}
                  {renderDateSection('Yesterday', groupedByDate.yesterday)}
                  {renderDateSection('Earlier', groupedByDate.earlier)}
                </div>
              )}
            </div>

            {/* Footer */}
            {totalNotifications > 0 && (
              <div className="p-3 border-t border-white/10 bg-white/5 flex items-center justify-between gap-2">
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0}
                  className={cn(
                    'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                    unreadCount > 0
                      ? 'bg-white/10 hover:bg-white/15 text-white'
                      : 'bg-white/5 text-white/40 cursor-not-allowed'
                  )}
                >
                  <Icons.CheckCheck className="w-4 h-4 inline mr-2" />
                  Mark All Read
                </button>

                <button
                  onClick={handleClearAll}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
                >
                  <Icons.Trash2 className="w-4 h-4 inline mr-2" />
                  Clear All
                </button>
              </div>
            )}

            {/* Decorative gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900/50 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
  );
};
